/**
 * ProgressChart - Exercise progress over time with trend lines
 */

import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { TrendingUp, ChevronDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';

// Exercise to muscle group mapping
const EXERCISE_MUSCLE_GROUPS = {
  // Göğüs (Chest)
  'Bench Press': 'Göğüs',
  'Chest Press Machine': 'Göğüs',
  'Pectoral Fly Machine': 'Göğüs',
  'Cable Crossover': 'Göğüs',
  'Cable Fly': 'Göğüs',
  'High Cable Crossover': 'Göğüs',
  'Dumbbell Fly': 'Göğüs',
  'Dumbbell Press': 'Göğüs',
  'Incline Dumbbell Fly': 'Göğüs',
  'Incline Bench Press': 'Göğüs',

  // Sırt (Back)
  'Lat Pulldown': 'Sırt',
  'Front Pulldown Machine': 'Sırt',
  'Seated Row Machine': 'Sırt',
  'Cable Seated Row': 'Sırt',
  'One Arm Dumbbell Row': 'Sırt',
  'Incline Dumbbell Row': 'Sırt',
  'Straight Arm Pulldown': 'Sırt',
  'Pull Up Machine': 'Sırt',
  'Back Extension Machine': 'Sırt',
  'Barbell Romanian Deadlift': 'Sırt',

  // Omuz (Shoulders)
  'Shoulder Press Machine': 'Omuz',
  'Dumbbell Shoulder Press': 'Omuz',
  'Dumbbell Lateral Raise': 'Omuz',
  'Seated Dumbbell Lateral Raise': 'Omuz',
  'Seated Dumbbell Front Raise': 'Omuz',
  'Cable Rope Front Raise': 'Omuz',
  'Cable Rear Deltoid Fly': 'Omuz',
  'Dumbbell Rear Deltoid Fly': 'Omuz',
  'Rear Deltoid Fly Machine': 'Omuz',
  'Arnold Press': 'Omuz',
  'Facepull': 'Omuz',

  // Biceps
  'Biceps Curl Machine': 'Biceps',
  'Cable Biceps Curl': 'Biceps',
  'EZ Bar Biceps Curl': 'Biceps',
  'EZ Bar Curl': 'Biceps',
  'Dumbbell Hammer Curl': 'Biceps',
  'Dumbbell Biceps Curl': 'Biceps',
  'Incline Dumbbell Biceps Curl': 'Biceps',

  // Triceps
  'Cable Triceps Pushdown': 'Triceps',
  'Triceps Press Machine': 'Triceps',

  // Bacak (Legs)
  'Leg Press Machine': 'Bacak',
  'Leg Extension': 'Bacak',
  'Leg Curl': 'Bacak',
  'Squat': 'Bacak',
  'Walking Lunge': 'Bacak',
  'Dumbbell Sumo Squat': 'Bacak',
  'Adductor': 'Bacak',
  'Abductor': 'Bacak',
  'Calf Raise': 'Bacak',
  'Barbell Hip Thrust': 'Bacak',

  // Core
  'Cable oblique twist': 'Core',
  'Cable Rope Crunch': 'Core',
  'Leg Raise': 'Core',
};

const MUSCLE_GROUP_ORDER = ['Göğüs', 'Sırt', 'Omuz', 'Biceps', 'Triceps', 'Bacak', 'Core', 'Diğer'];

const ProgressChart = ({ workouts }) => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [metric, setMetric] = useState('weight'); // weight, volume, reps

  // Get unique exercises grouped by muscle group and sorted alphabetically
  const groupedExercises = useMemo(() => {
    // Get unique exercises with counts
    const counts = {};
    workouts.forEach(w => {
      counts[w.exercise_name] = (counts[w.exercise_name] || 0) + 1;
    });

    // Group by muscle group
    const groups = {};
    MUSCLE_GROUP_ORDER.forEach(group => {
      groups[group] = [];
    });

    Object.keys(counts).forEach(exercise => {
      const group = EXERCISE_MUSCLE_GROUPS[exercise] || 'Diğer';
      if (!groups[group]) groups[group] = [];
      groups[group].push({ name: exercise, count: counts[exercise] });
    });

    // Sort each group alphabetically
    Object.keys(groups).forEach(group => {
      groups[group].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    });

    // Remove empty groups
    return MUSCLE_GROUP_ORDER
      .filter(group => groups[group]?.length > 0)
      .map(group => ({ group, exercises: groups[group] }));
  }, [workouts]);

  // Flat list for default selection (by frequency)
  const exercises = useMemo(() => {
    const counts = {};
    workouts.forEach(w => {
      counts[w.exercise_name] = (counts[w.exercise_name] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [workouts]);

  // Set default exercise
  useMemo(() => {
    if (!selectedExercise && exercises.length > 0) {
      setSelectedExercise(exercises[0]);
    }
  }, [exercises, selectedExercise]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!selectedExercise) return [];

    const exerciseWorkouts = workouts
      .filter(w => w.exercise_name === selectedExercise)
      .sort((a, b) => a.date.localeCompare(b.date));

    // Group by date and get max values
    const dateMap = {};
    exerciseWorkouts.forEach(w => {
      const date = w.date;
      if (!dateMap[date]) {
        dateMap[date] = {
          date,
          maxWeight: 0,
          totalVolume: 0,
          maxReps: 0,
          sets: 0,
        };
      }
      const weight = parseFloat(w.weight || 0);
      const reps = w.reps || 0;
      dateMap[date].maxWeight = Math.max(dateMap[date].maxWeight, weight);
      dateMap[date].totalVolume += weight * reps;
      dateMap[date].maxReps = Math.max(dateMap[date].maxReps, reps);
      dateMap[date].sets++;
    });

    return Object.values(dateMap).map(d => ({
      ...d,
      dateLabel: format(parseISO(d.date), 'd MMM', { locale: tr }),
    }));
  }, [workouts, selectedExercise]);

  // Calculate trend
  const trend = useMemo(() => {
    if (chartData.length < 2) return { direction: 'stable', percentage: 0 };

    const values = chartData.map(d => {
      if (metric === 'weight') return d.maxWeight;
      if (metric === 'volume') return d.totalVolume;
      return d.maxReps;
    });

    const firstHalf = values.slice(0, Math.floor(values.length / 2));
    const secondHalf = values.slice(Math.floor(values.length / 2));

    const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
    const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;

    if (avgFirst === 0) return { direction: 'stable', percentage: 0 };

    const change = ((avgSecond - avgFirst) / avgFirst) * 100;

    return {
      direction: change > 5 ? 'up' : change < -5 ? 'down' : 'stable',
      percentage: Math.abs(change).toFixed(1),
    };
  }, [chartData, metric]);

  const getMetricValue = (d) => {
    if (metric === 'weight') return d.maxWeight;
    if (metric === 'volume') return d.totalVolume;
    return d.maxReps;
  };

  const metricLabels = {
    weight: 'Maks Ağırlık (kg)',
    volume: 'Toplam Hacim (kg)',
    reps: 'Maks Tekrar',
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium">{label}</p>
          <p className="text-orange-400">
            {metric === 'weight' && `${data.maxWeight} kg`}
            {metric === 'volume' && `${data.totalVolume.toFixed(0)} kg hacim`}
            {metric === 'reps' && `${data.maxReps} tekrar`}
          </p>
          <p className="text-gray-400 text-sm">{data.sets} set</p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900 border border-gray-800 rounded-xl p-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-white">Egzersiz İlerlemesi</h3>
          {trend.direction !== 'stable' && (
            <span className={`text-xs px-2 py-0.5 rounded ${
              trend.direction === 'up'
                ? 'bg-green-500/20 text-green-400'
                : 'bg-red-500/20 text-red-400'
            }`}>
              {trend.direction === 'up' ? '↑' : '↓'} %{trend.percentage}
            </span>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* Exercise Selector - Grouped by muscle */}
        <div className="relative">
          <select
            value={selectedExercise || ''}
            onChange={(e) => setSelectedExercise(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white cursor-pointer max-w-[200px]"
          >
            {groupedExercises.map(({ group, exercises: groupExercises }) => (
              <optgroup key={group} label={`── ${group} ──`}>
                {groupExercises.map(({ name, count }) => (
                  <option key={name} value={name}>
                    {name} ({count})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Metric Selector */}
        <div className="flex bg-gray-800 rounded-lg p-0.5">
          {Object.entries(metricLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setMetric(key)}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                metric === key
                  ? 'bg-orange-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {key === 'weight' ? 'Ağırlık' : key === 'volume' ? 'Hacim' : 'Tekrar'}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 ? (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="dateLabel"
                stroke="#9CA3AF"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                stroke="#9CA3AF"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey={metric === 'weight' ? 'maxWeight' : metric === 'volume' ? 'totalVolume' : 'maxReps'}
                stroke="#f97316"
                strokeWidth={2}
                dot={{ fill: '#f97316', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#f97316' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center text-gray-500">
          Bu egzersiz için veri yok
        </div>
      )}
    </motion.div>
  );
};

export default ProgressChart;
